import { useEffect, useState, useRef } from 'react'

export interface DeviceCurrentValues {
  device_id: number
  physical_id: string
  title: string
  values: {
    ActivePower?: number
    'Generated Power'?: number
    temperature?: number
    [key: string]: number | undefined
  }
}

export const useDeviceCurrentValues = () => {
  const [deviceCurrentValues, setDeviceCurrentValues] = useState<Map<string, DeviceCurrentValues>>(
    new Map()
  )
  const [isConnected, setIsConnected] = useState(false)
  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    const connectWebSocket = () => {
      const token = window.localStorage.getItem('aiot_t')

      const wsUrl = process.env.REACT_APP_WS_URL
      const socket = new WebSocket(`${wsUrl}/ws/device_current_values/?token=${token}`)

      socket.onopen = () => {
        setIsConnected(true)
      }

      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)

          if (message.type === 'initial') {
            const devicesMap = new Map<string, DeviceCurrentValues>()
            message.devices?.forEach((device: DeviceCurrentValues) => {
              devicesMap.set(device.physical_id, device)
            })
            setDeviceCurrentValues(devicesMap)
          } else {
            setDeviceCurrentValues((prev) => {
              const newMap = new Map(prev)
              const existingDevice = newMap.get(message.physical_id)

              if (existingDevice) {
                newMap.set(message.physical_id, {
                  ...existingDevice,
                  values: {
                    ...existingDevice.values,
                    ...message.values,
                  },
                })
              } else {
                newMap.set(message.physical_id, {
                  device_id: 0,
                  physical_id: message.physical_id,
                  title: message.physical_id,
                  values: message.values,
                })
              }

              return newMap
            })
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }

      socket.onerror = (error) => {
        setIsConnected(false)
      }

      socket.onclose = () => {
        setIsConnected(false)
        // Attempt to reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect WebSocket...')
          connectWebSocket()
        }, 3000)
      }

      socketRef.current = socket
    }

    connectWebSocket()

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.close()
      }
    }
  }, [])

  const getCurrentValue = (physicalId: string, parameter: string): number | undefined => {
    const device = deviceCurrentValues.get(physicalId)
    return device?.values[parameter]
  }

  return {
    deviceCurrentValues,
    isConnected,
    getCurrentValue,
  }
}
